import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Descriptions, Button, Space, Tag, Spin, Alert } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getAuditLog, type AuditLog } from '@/api/auditApi';

const ACTION_COLORS: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
};

export default function AuditLogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [log, setLog] = useState<AuditLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getAuditLog(id)
      .then((data) => setLog(data))
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [id, t]);

  if (loading) return <Spin size="large" />;
  if (error) return <Alert type="error" message={error} />;
  if (!log) return <Alert type="warning" message={t('app.not_found')} />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/audit')}>
          {t('app.back')}
        </Button>
      </Space>

      <h2>{t('audit.detail_title')}</h2>

      <Descriptions bordered column={2}>
        <Descriptions.Item label={t('audit.field.action')}>
          <Tag color={ACTION_COLORS[log.action] ?? 'default'}>
            {t(`audit.action_options.${log.action}`, log.action)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('audit.field.resource')}>
          {log.resource}
        </Descriptions.Item>
        <Descriptions.Item label={t('audit.field.resource_id')}>
          {log.resource_id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('audit.field.user_id')}>
          {log.user_id}
        </Descriptions.Item>
        <Descriptions.Item label={t('audit.field.timestamp')} span={2}>
          {new Date(log.timestamp).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t('audit.field.ip_address')}>
          {log.ip_address || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('audit.field.user_agent')}>
          {log.user_agent || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('audit.field.details')} span={2}>
          {log.details ? (
            <pre style={{ margin: 0, fontSize: 12, maxHeight: 300, overflow: 'auto' }}>
              {(() => {
                try { return JSON.stringify(JSON.parse(log.details), null, 2); }
                catch { return log.details; }
              })()}
            </pre>
          ) : '-'}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
}
